import { useState, useEffect, useCallback, useRef } from 'react';
import { OnboardingResponse, OnboardingStepId, OnboardingPrerequisites, DEFAULT_ONBOARDING_RESPONSE } from '@optidev/shared-types';
import { onboardingApi, onboardingEvents } from '@/lib/api/onboarding';
import { analytics } from '@lib/analytics';
import { firebaseAuth } from '@/lib/firebase';
import { useAuthToken } from './useAuthToken';

const COLLAPSE_KEY = 'optidev_onboarding_collapsed';

function getCollapseState(): boolean {
  return localStorage.getItem(COLLAPSE_KEY) === 'true';
}

function setCollapseState(collapsed: boolean): void {
  localStorage.setItem(COLLAPSE_KEY, String(collapsed));
}

/** Build user-scoped localStorage key */
function getUserKey(suffix: string): string | null {
  const uid = firebaseAuth.currentUser?.uid;
  return uid ? `onb:${uid}:${suffix}` : null;
}

/**
 * Check localStorage to determine if onboarding is fully done (all steps or dismissed).
 * If done, skip the API call entirely — zero network requests for completed users.
 */
function isOnboardingDoneLocally(): boolean {
  const uid = firebaseAuth.currentUser?.uid;
  if (!uid) return false;

  // Dismissed → no need to fetch
  if (localStorage.getItem(`onb:${uid}:dismissed`) === 'true') return true;

  // All 3 steps marked → no need to fetch
  const step1 = localStorage.getItem(`onb:${uid}:step1_marked`) === 'true';
  const step2 = localStorage.getItem(`onb:${uid}:step2_marked`) === 'true';
  const step3 = localStorage.getItem(`onb:${uid}:step3_marked`) === 'true';

  return step1 && step2 && step3;
}

export interface UseOnboardingReturn {
  showChecklist: boolean;
  completedSteps: OnboardingStepId[];
  recommendedStep: OnboardingStepId | null;
  prerequisites: OnboardingPrerequisites;
  lastActiveProjectId: string | null;
  isLoading: boolean;
  error: Error | null;
  isCollapsed: boolean;
  dismiss: () => Promise<void>;
  refetch: () => void;
  toggleCollapse: () => void;
  markFirstEditIfNeeded: () => void;
}

/**
 * Hook for managing onboarding checklist state.
 *
 * Network strategy: fetch once on mount (skipped entirely if localStorage says done),
 * then only refetch after a step is marked. No polling, no route-change refetch.
 */
export function useOnboarding(): UseOnboardingReturn {
  const { token, loading: tokenLoading } = useAuthToken();
  const [data, setData] = useState<OnboardingResponse>(DEFAULT_ONBOARDING_RESPONSE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => getCollapseState());
  const calledThisSession = useRef(false);

  const fetchProgress = useCallback(async () => {
    if (!token) return;

    try {
      const response = await onboardingApi.getProgress(token);
      setData(response);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch onboarding progress'));
      console.error('Failed to fetch onboarding progress:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch — skipped entirely if localStorage says onboarding is done
  useEffect(() => {
    if (!tokenLoading && token) {
      if (isOnboardingDoneLocally()) {
        // All steps done or dismissed — no API call needed
        setLoading(false);
        return;
      }
      setLoading(true);
      fetchProgress();
    } else if (!tokenLoading && !token) {
      setLoading(false);
    }
  }, [tokenLoading, token]);

  // Subscribe to step-completion events from markStepIfNeeded (non-React code).
  // Only fires once per step due to localStorage + in-flight dedup in markStepIfNeeded.
  useEffect(() => {
    return onboardingEvents.subscribe(() => fetchProgress());
  }, [fetchProgress]);

  const dismiss = useCallback(async () => {
    if (!token) return;

    try {
      await onboardingApi.dismiss(token);
      // Persist in localStorage so future sessions skip the API call
      const key = getUserKey('dismissed');
      if (key) localStorage.setItem(key, 'true');
      setData((prev) => ({ ...prev, showChecklist: false }));
    } catch (err) {
      console.error('Failed to dismiss onboarding:', err);
    }
  }, [token]);

  const refetch = useCallback(() => {
    fetchProgress();
  }, [fetchProgress]);

  const toggleCollapse = useCallback(() => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    setCollapseState(newState);
  }, [isCollapsed]);

  const markFirstEditIfNeeded = useCallback(() => {
    // Gate 1: already done server-side (cheapest — in-memory boolean)
    if (data.prerequisites.step2Completed) return;

    // Gate 2: checklist not showing (dismissed/completed/ineligible)
    if (!data.showChecklist) return;

    // Gate 3: already called this session (in-memory ref)
    if (calledThisSession.current) return;

    // Gate 4: user-scoped localStorage dedup (persists across sessions)
    const step2Key = getUserKey('step2_marked');
    if (!step2Key) return; // No UID yet
    // Clear stale flag if server says step2 is NOT completed (handles DB resets)
    if (localStorage.getItem(step2Key) === 'true') {
      // Server is the source of truth — if it says not completed, the flag is stale
      localStorage.removeItem(step2Key);
    }

    if (!token) return;

    // Optimistic guard — prevents duplicate calls this session
    calledThisSession.current = true;

    onboardingApi.markFirstEdit(token)
      .then((response) => {
        // Pessimistic — only persist after confirmed success
        const key = getUserKey('step2_marked');
        if (key) localStorage.setItem(key, 'true');
        // Notify event bus → triggers refetch via subscription
        onboardingEvents._emit();
        // Only fire analytics when backend confirms this is truly the first time
        if (response.marked) {
          analytics.track('first_project_edit');
        }
      })
      .catch(() => {
        // Allow retry on next edit action
        calledThisSession.current = false;
      });
  }, [data.prerequisites.step2Completed, data.showChecklist, token]);

  return {
    showChecklist: data.showChecklist,
    completedSteps: data.completedSteps,
    recommendedStep: data.recommendedStep,
    prerequisites: data.prerequisites,
    lastActiveProjectId: data.lastActiveProjectId,
    isLoading: loading || tokenLoading,
    error,
    isCollapsed,
    dismiss,
    refetch,
    toggleCollapse,
    markFirstEditIfNeeded,
  };
}
